import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { Button, Card, Spin, message, Modal, Space, Tooltip, Select, Radio, Input, Popconfirm } from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  EditOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LinkOutlined,
  TranslationOutlined,
  HighlightOutlined,
  BookOutlined,
  DeleteOutlined,
  SearchOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  literatureService,
  Literature,
  getLiteraturePdfUrl,
  listLiteraturePdfAttachments,
  literatureHasPdfAttachment
} from '../../services/literature/literatureService';
import { translationService } from '../../services/literature/translationService';
import { LiteratureLimsShell } from '../../components/literature/LiteratureLimsShell';
import { randomClientId } from '../../utils/clientRandomId';
import { coerceNotesToHtmlForEditor, appendTranslationToNotesHtml } from '../../utils/literatureNotesHtml';
import { normalizeNotesHtmlToImgEmbed } from '../../utils/literatureNotesImageStub';
import RichTextEditor from '../../components/RichTextEditor';
import '../../styles/pdf-reader-stitch.css';

function getRelatedIdsFromLiterature(lit: Literature | null): string[] {
  if (!lit) return [];
  if (Array.isArray(lit.relatedLiteratures) && lit.relatedLiteratures.length) {
    return lit.relatedLiteratures.map((r) => r._id);
  }
  const raw = lit.relatedLiteratureIds;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === 'string' ? x : String((x as { _id?: string })._id ?? '')))
    .filter(Boolean);
}

type ReaderMode = 'read' | 'translate' | 'annotate';

/** 相对 PDF 页面包盒的百分比，用于缩放后仍对齐 */
type HighlightRectPct = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type PdfAnnotation = {
  id: string;
  text: string;
  color: string;
  note: string;
  page?: number;
  createdAt: number;
  rects?: HighlightRectPct[];
};

/** 与 Stitch 稿一致的标注色（黄 / 蓝 / 绿 / 粉 / 紫） */
const ANNOT_COLORS = ['#fde047', '#93c5fd', '#86efac', '#fca5a5', '#d8b4fe'] as const;

function rangeToPctRects(range: Range, pageEl: HTMLElement): HighlightRectPct[] {
  const pr = pageEl.getBoundingClientRect();
  if (pr.width < 1 || pr.height < 1) return [];
  let rects = Array.from(range.getClientRects()).filter((r) => r.width > 0.5 && r.height > 0.5);
  if (rects.length === 0) {
    const b = range.getBoundingClientRect();
    if (b.width > 0 && b.height > 0) rects = [b];
  }
  return rects.map((r) => ({
    left: ((r.left - pr.left) / pr.width) * 100,
    top: ((r.top - pr.top) / pr.height) * 100,
    width: Math.max((r.width / pr.width) * 100, 0.08),
    height: Math.max((r.height / pr.height) * 100, 0.12)
  }));
}

function bubbleAnchorFromRange(range: Range, pageEl: HTMLElement): { topPct: number; leftPct: number; widthPct: number } | null {
  const pr = pageEl.getBoundingClientRect();
  if (pr.width < 1 || pr.height < 1) return null;
  let rects = Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0);
  if (rects.length === 0) {
    const b = range.getBoundingClientRect();
    rects = [b];
  }
  const minL = Math.min(...rects.map((r) => r.left));
  const maxR = Math.max(...rects.map((r) => r.right));
  const maxB = Math.max(...rects.map((r) => r.bottom));
  const topPct = ((maxB - pr.top + 8) / pr.height) * 100;
  return {
    leftPct: Math.max(0, ((minL - pr.left) / pr.width) * 100),
    topPct: Math.max(0, Math.min(86, topPct)),
    widthPct: Math.min(96, Math.max(28, ((maxR - minL) / pr.width) * 100))
  };
}

type DocTranslationAnchor = { page: number; topPct: number; leftPct: number; widthPct: number };

const annotationsStorageKey = (literatureId: string) => `pdfReaderAnnotations:${literatureId}`;

// Worker 必须作为独立静态脚本加载；用 new URL(..., import.meta.url) 会让 webpack 打包进带 require 的 chunk，
// 在 Worker 里执行会报 "require is not defined"，并触发 fake worker / WorkerMessageHandler 错误。
// public/pdf.worker.min.js 由 scripts/copy-pdf-worker.js（postinstall）从 pdfjs-dist 复制。
const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
pdfjs.GlobalWorkerOptions.workerSrc = `${publicBase}/pdf.worker.min.js`;

/** 共享页等通过 ?pdf= 传入快照 URL 时的白名单（避免 javascript: 等） */
function sanitizePdfUrlQueryParam(raw: string | null | undefined): string {
  const t = (raw || '').trim();
  if (!t) return '';
  if (/^javascript:/i.test(t)) return '';
  return t;
}

function openArticlePageUrl(raw: string) {
  const t = raw.trim();
  if (!t) return;
  let url = t;
  if (/^https?:\/\//i.test(url)) {
    /* ok */
  } else if (url.startsWith('//')) {
    url = `${window.location.protocol}${url}`;
  } else {
    url = `https://${url}`;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const PDFViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const attachmentIdFromUrl = searchParams.get('attachmentId');
  const pdfFromQuery = sanitizePdfUrlQueryParam(searchParams.get('pdf'));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [literature, setLiterature] = useState<Literature | null>(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [scale, setScale] = useState(1.0);
  const [readerMode, setReaderMode] = useState<ReaderMode>('read');
  const [annotateColor, setAnnotateColor] = useState<string>(ANNOT_COLORS[0]);
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);
  const [selectedText, setSelectedText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [translating, setTranslating] = useState(false);
  const selectedTextRef = useRef('');
  const [docTranslationAnchor, setDocTranslationAnchor] = useState<DocTranslationAnchor | null>(null);
  const pdfScrollRef = useRef<HTMLDivElement>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showExpandEditor, setShowExpandEditor] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const openNotesExpandEditor = useCallback(() => {
    setNotesDraft((prev) => normalizeNotesHtmlToImgEmbed(prev));
    setShowExpandEditor(true);
  }, []);

  const [pickPdfChoice, setPickPdfChoice] = useState<string>('');
  const [annotationsHydrated, setAnnotationsHydrated] = useState(false);
  const [visiblePage, setVisiblePage] = useState(1);
  const [lastNotesSavedAt, setLastNotesSavedAt] = useState<string | null>(null);
  const [translationBubbleDismissed, setTranslationBubbleDismissed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [protocolSearch, setProtocolSearch] = useState('');
  const [pageJumpDraft, setPageJumpDraft] = useState('1');
  const [relatedPickOpen, setRelatedPickOpen] = useState(false);
  const [pickKeyword, setPickKeyword] = useState('');
  const [pickOptions, setPickOptions] = useState<Literature[]>([]);
  const [pickLoading, setPickLoading] = useState(false);
  const [selectedToAddId, setSelectedToAddId] = useState<string | undefined>(undefined);
  const [savingRelated, setSavingRelated] = useState(false);

  const scrollToPdfPage = useCallback(
    (page: number) => {
      if (!numPages || page < 1 || page > numPages) return;
      const root = pdfScrollRef.current;
      const el = root?.querySelector(`[data-pdf-page="${page}"]`);
      (el as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [numPages]
  );

  const orphanAnnotations = useMemo(
    () => annotations.filter((a) => typeof a.page !== 'number'),
    [annotations]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchLiterature();
    return () => window.removeEventListener('resize', handleResize);
  }, [id]);

  const pdfList = useMemo(() => (literature ? listLiteraturePdfAttachments(literature) : []), [literature]);

  useEffect(() => {
    if (!literature) return;
    const list = listLiteraturePdfAttachments(literature);
    if (list.length > 1) {
      setPickPdfChoice((prev) => {
        if (prev && list.some((x) => x.id === prev)) return prev;
        return list[0].id;
      });
    }
  }, [literature]);

  const rawPdfSource = useMemo(() => {
    if (!literature) return '';
    if (pdfList.length === 0) {
      const fromLit = getLiteraturePdfUrl(literature);
      if (fromLit) return fromLit;
      return pdfFromQuery || '';
    }
    if (pdfList.length === 1) return pdfList[0].url;
    if (attachmentIdFromUrl) {
      const found = pdfList.find((p) => p.id === attachmentIdFromUrl);
      return found?.url || pdfList[0].url;
    }
    return '';
  }, [literature, pdfList, attachmentIdFromUrl, pdfFromQuery]);

  const pageVisibilityRatiosRef = useRef<Map<number, number>>(new Map());
  useEffect(() => {
    const root = pdfScrollRef.current;
    if (!root || !numPages || pdfLoading || !rawPdfSource) return;
    const pages = root.querySelectorAll('[data-pdf-page]');
    if (pages.length === 0) return;
    pageVisibilityRatiosRef.current = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const n = Number((e.target as HTMLElement).getAttribute('data-pdf-page'));
          if (Number.isFinite(n)) pageVisibilityRatiosRef.current.set(n, e.intersectionRatio);
        }
        let best = 1;
        let bestR = -1;
        pageVisibilityRatiosRef.current.forEach((r, n) => {
          if (r > bestR) {
            bestR = r;
            best = n;
          }
        });
        if (bestR >= 0) setVisiblePage(best);
      },
      { root, rootMargin: '-12% 0px -12% 0px', threshold: [0, 0.05, 0.1, 0.2, 0.35, 0.5, 0.75, 1] }
    );
    pages.forEach((p) => io.observe(p));
    return () => io.disconnect();
  }, [numPages, pdfLoading, rawPdfSource]);

  useEffect(() => {
    if (!rawPdfSource) return;
    setPdfLoading(true);
    setNumPages(null);
  }, [rawPdfSource]);

  useEffect(() => {
    selectedTextRef.current = selectedText;
  }, [selectedText]);

  useEffect(() => {
    if (!id) return;
    setAnnotationsHydrated(false);
    try {
      const raw = localStorage.getItem(annotationsStorageKey(id));
      if (!raw) {
        setAnnotations([]);
        setAnnotationsHydrated(true);
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        setAnnotations(
          parsed.filter(
            (x): x is PdfAnnotation =>
              x &&
              typeof x === 'object' &&
              typeof (x as PdfAnnotation).id === 'string' &&
              typeof (x as PdfAnnotation).text === 'string'
          )
        );
      } else setAnnotations([]);
    } catch {
      setAnnotations([]);
    } finally {
      setAnnotationsHydrated(true);
    }
  }, [id]);

  useEffect(() => {
    if (!id || !annotationsHydrated) return;
    try {
      localStorage.setItem(annotationsStorageKey(id), JSON.stringify(annotations));
    } catch {
      /* quota / private mode */
    }
  }, [id, annotations, annotationsHydrated]);

  useEffect(() => {
    if (readerMode !== 'translate') {
      setSelectedText('');
      setTranslatedText('');
      setTranslating(false);
      setDocTranslationAnchor(null);
    }
  }, [readerMode]);

  useEffect(() => {
    if (readerMode !== 'translate') return;
    const q = selectedText.trim();
    if (!q) {
      setTranslatedText('');
      setTranslating(false);
      setDocTranslationAnchor(null);
      return;
    }
    setTranslating(true);
    const snapshot = q;
    const tid = window.setTimeout(async () => {
      try {
        const result = await translationService.translate({
          text: snapshot,
          fromLang: 'en',
          toLang: 'zh'
        });
        if (selectedTextRef.current.trim() !== snapshot) return;
        setTranslatedText(result.translatedText);
      } catch (error: unknown) {
        if (selectedTextRef.current.trim() === snapshot) {
          message.error(error instanceof Error ? error.message : '翻译失败');
        }
      } finally {
        if (selectedTextRef.current.trim() === snapshot) {
          setTranslating(false);
        }
      }
    }, 420);
    return () => clearTimeout(tid);
  }, [selectedText, readerMode]);

  const fetchLiterature = async () => {
    if (!id) return;
    try {
      const data = await literatureService.getDetail(id);
      setLiterature(data);
      setNotesDraft(coerceNotesToHtmlForEditor(data.notes || ''));
      setLoading(false);
    } catch (error: any) {
      message.error(error.message || '获取文献信息失败');
      navigate('/literature');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF加载错误:', error);
    message.error('PDF文件加载失败');
    setPdfLoading(false);
  };

  const onPdfMouseUp = useCallback(() => {
    if (readerMode === 'read') return;

    const selection = window.getSelection();
    const text = selection?.toString().trim() ?? '';
    if (!text) return;

    if (readerMode === 'translate') {
      setTranslationBubbleDismissed(false);
      setSelectedText(text);
      return;
    }

    if (readerMode === 'annotate') {
      if (!selection || selection.rangeCount === 0) return;
      let page: number | undefined;
      let rects: HighlightRectPct[] | undefined;
      const anchor = selection.anchorNode;
      let pageEl: HTMLElement | null = null;
      if (anchor) {
        const startEl =
          anchor.nodeType === Node.TEXT_NODE
            ? (anchor.parentElement as HTMLElement | null)
            : anchor instanceof HTMLElement
              ? anchor
              : null;
        pageEl = startEl?.closest?.('[data-pdf-page]') as HTMLElement | null;
        if (pageEl) {
          const n = Number(pageEl.getAttribute('data-pdf-page'));
          if (Number.isFinite(n)) page = n;
        }
      }
      try {
        const range = selection.getRangeAt(0);
        if (pageEl) rects = rangeToPctRects(range, pageEl);
      } catch {
        rects = undefined;
      }
      const newId = randomClientId();
      setAnnotations((prev) => [
        ...prev,
        {
          id: newId,
          text,
          color: annotateColor,
          note: '',
          page,
          rects,
          createdAt: Date.now()
        }
      ]);
      message.success('已添加标注');
      try {
        selection.removeAllRanges();
      } catch {
        /* ignore */
      }
    }
  }, [readerMode, annotateColor]);

  const removeAnnotation = useCallback((aid: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== aid));
  }, []);

  const updateAnnotationNote = useCallback((aid: string, note: string) => {
    setAnnotations((prev) => prev.map((a) => (a.id === aid ? { ...a, note } : a)));
  }, []);

  const clearAllAnnotations = useCallback(() => {
    setAnnotations([]);
    message.success('已清空标注');
  }, []);

  const updateDocTranslationAnchor = useCallback(() => {
    if (readerMode !== 'translate') return;
    const text = selectedTextRef.current.trim();
    if (!text) {
      setDocTranslationAnchor(null);
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.toString().trim() !== text) return;
    let range: Range;
    try {
      range = sel.getRangeAt(0);
    } catch {
      return;
    }
    const anchorNode = sel.anchorNode;
    const startEl =
      anchorNode?.nodeType === Node.TEXT_NODE
        ? (anchorNode.parentElement as HTMLElement | null)
        : anchorNode instanceof HTMLElement
          ? anchorNode
          : null;
    const pageEl = startEl?.closest?.('[data-pdf-page]') as HTMLElement | null;
    if (!pageEl) return;
    const page = Number(pageEl.getAttribute('data-pdf-page'));
    if (!Number.isFinite(page)) return;
    const pos = bubbleAnchorFromRange(range, pageEl);
    if (!pos) return;
    setDocTranslationAnchor({ page, ...pos });
  }, [readerMode]);

  useEffect(() => {
    if (readerMode !== 'translate' || !selectedText.trim()) {
      setDocTranslationAnchor(null);
      return;
    }
    const fid = requestAnimationFrame(() => updateDocTranslationAnchor());
    return () => cancelAnimationFrame(fid);
  }, [selectedText, readerMode, updateDocTranslationAnchor, scale]);

  useEffect(() => {
    const el = pdfScrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateDocTranslationAnchor();
      });
    };
    el.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      el.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [updateDocTranslationAnchor, numPages]);

  useEffect(() => {
    if (!translatedText || readerMode !== 'translate') return;
    const fid = requestAnimationFrame(() => updateDocTranslationAnchor());
    return () => cancelAnimationFrame(fid);
  }, [translatedText, readerMode, updateDocTranslationAnchor, scale]);

  const handleSaveNotes = async () => {
    if (!id) return;
    setSavingNotes(true);
    try {
      const updated = await literatureService.update(id, { notes: notesDraft });
      setLiterature(updated);
      setLastNotesSavedAt(
        new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
      message.success('笔记已保存');
    } catch (error: any) {
      message.error(error.message || '保存笔记失败');
    } finally {
      setSavingNotes(false);
    }
  };

  useEffect(() => {
    if (!relatedPickOpen || !id) return;
    let cancelled = false;
    setPickLoading(true);
    const t = window.setTimeout(() => {
      void literatureService
        .getList({
          page: 1,
          limit: 200,
          ...(pickKeyword.trim() ? { keyword: pickKeyword.trim() } : {}),
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })
        .then((res: { literatures?: Literature[] }) => {
          if (!cancelled) {
            setPickOptions(res.literatures || []);
            setPickLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setPickLoading(false);
        });
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [relatedPickOpen, id, pickKeyword]);

  const handleConfirmAddRelated = async () => {
    if (!id || !literature || !selectedToAddId) {
      message.warning('请选择一篇文献');
      return;
    }
    setSavingRelated(true);
    try {
      const base = getRelatedIdsFromLiterature(literature);
      const ids = base.includes(selectedToAddId) ? base : [...base, selectedToAddId];
      const updated = await literatureService.update(id, { relatedLiteratureIds: ids });
      setLiterature(updated);
      message.success('已关联');
      setRelatedPickOpen(false);
      setSelectedToAddId(undefined);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : '关联失败');
    } finally {
      setSavingRelated(false);
    }
  };

  const handleRemoveRelated = async (rid: string) => {
    if (!id || !literature) return;
    setSavingRelated(true);
    try {
      const ids = getRelatedIdsFromLiterature(literature).filter((x) => x !== rid);
      const updated = await literatureService.update(id, { relatedLiteratureIds: ids });
      setLiterature(updated);
      message.success('已移除关联');
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : '移除失败');
    } finally {
      setSavingRelated(false);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1.0);
  };

  const handleExportAnnotationSummary = useCallback(() => {
    if (!literature || annotations.length === 0) {
      message.warning('暂无标注可导出');
      return;
    }
    const lines = [
      `# ${literature.title}`,
      '',
      ...annotations.map(
        (a) =>
          `## p.${a.page ?? '?'} — ${new Date(a.createdAt).toLocaleString('zh-CN')}\n${a.text}\n批注：${(a.note || '—').trim()}\n`
      )
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `pdf-annotations-${id || 'export'}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    message.success('已导出标注摘要');
  }, [annotations, id, literature]);

  const handleCopyTranslation = useCallback(async () => {
    const t = translatedText.trim();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      message.success('已复制译文');
    } catch {
      message.error('复制失败，请手动选择复制');
    }
  }, [translatedText]);

  const handleSaveTranslationAsNote = useCallback(() => {
    const t = translatedText.trim();
    if (!t) return;
    setNotesDraft((prev) => appendTranslationToNotesHtml(prev, t));
    message.success('已追加到笔记草稿，记得保存');
  }, [translatedText]);

  const inspectBack = () => navigate(`/literature?inspect=${encodeURIComponent(id || '')}`);

  const navShared = location.pathname.startsWith('/literature/shared');
  const navNotebook = location.pathname.startsWith('/literature/notebook');
  const navLibrary = location.pathname.startsWith('/literature') && !navShared && !navNotebook;

  useEffect(() => {
    setPageJumpDraft(String(visiblePage));
  }, [visiblePage]);

  const commitPageJump = useCallback(() => {
    const n = parseInt(pageJumpDraft, 10);
    if (!Number.isFinite(n) || !numPages) return;
    const p = Math.min(numPages, Math.max(1, n));
    scrollToPdfPage(p);
    setPageJumpDraft(String(p));
  }, [numPages, pageJumpDraft, scrollToPdfPage]);

  const readerToolbarHint =
    readerMode === 'read'
      ? '阅读模式：已有高亮在正文与侧栏始终可见；选中文本不会产生新的翻译或标注。'
      : readerMode === 'translate'
        ? '翻译模式：已有高亮始终可见；划选后译文显示在浮层，可复制或写入研究笔记。'
        : '标注模式：划选可在正文新增高亮并同步到右侧列表（数据保存在本机浏览器）。';

  if (loading) {
    return (
      <LiteratureLimsShell
        crumbs={[
          { label: '首页', path: '/dashboard' },
          { label: '文献管理', path: '/literature' },
          { label: 'PDF 阅读' }
        ]}
        title="加载 PDF"
        subtitle="正在获取文献与附件…"
      >
        <div className="mm-table-shell" style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </LiteratureLimsShell>
    );
  }

  const canOpenPdf = literatureHasPdfAttachment(literature) || !!pdfFromQuery;
  if (!literature || !canOpenPdf) {
    return (
      <LiteratureLimsShell
        crumbs={[
          { label: '首页', path: '/dashboard' },
          { label: '文献管理', path: '/literature' },
          { label: 'PDF 阅读' }
        ]}
        title="无法打开 PDF"
        subtitle="该文献没有可用的 PDF 附件。"
        headActions={
          <Button icon={<ArrowLeftOutlined />} onClick={inspectBack}>
            返回检视
          </Button>
        }
      >
        <div className="mm-table-shell" style={{ padding: 24 }}>
          <Card className="lit-form-card" bordered={false}>
            <p style={{ margin: 0 }}>该文献没有可用的 PDF 文件</p>
          </Card>
        </div>
      </LiteratureLimsShell>
    );
  }

  if (!rawPdfSource && pdfList.length > 1) {
    return (
      <LiteratureLimsShell
        crumbs={[
          { label: '首页', path: '/dashboard' },
          { label: '文献管理', path: '/literature' },
          { label: 'PDF 阅读' }
        ]}
        title="选择 PDF 附件"
        subtitle="该文献包含多个 PDF 附件，请选择要打开的文件。"
        headActions={
          <Button icon={<ArrowLeftOutlined />} onClick={inspectBack}>
            返回检视
          </Button>
        }
      >
        <div className="mm-table-shell" style={{ padding: 24 }}>
        <Card className="lit-form-card" bordered={false}>
          <p>该文献包含多个 PDF 附件，请选择要打开的文件。</p>
        </Card>
        <Modal
          title="选择要打开的 PDF"
          open={true}
          closable
          onCancel={inspectBack}
          okText="打开"
          onOk={() => {
            setSearchParams({ attachmentId: pickPdfChoice }, { replace: true });
          }}
        >
          <Radio.Group
            value={pickPdfChoice || pdfList[0]?.id || ''}
            onChange={(e) => setPickPdfChoice(e.target.value)}
          >
            {pdfList.map((p) => (
              <Radio key={p.id} value={p.id} style={{ display: 'block', marginBottom: 8 }}>
                {p.name}
              </Radio>
            ))}
          </Radio.Group>
        </Modal>
        </div>
      </LiteratureLimsShell>
    );
  }

  if (!rawPdfSource) {
    return (
      <LiteratureLimsShell
        crumbs={[
          { label: '首页', path: '/dashboard' },
          { label: '文献管理', path: '/literature' },
          { label: 'PDF 阅读' }
        ]}
        title="无法解析 PDF"
        subtitle="未找到可用的 PDF 地址。"
        headActions={
          <Button icon={<ArrowLeftOutlined />} onClick={inspectBack}>
            返回检视
          </Button>
        }
      >
        <div className="mm-table-shell" style={{ padding: 24 }}>
          <Card className="lit-form-card" bordered={false}>
            <p style={{ margin: 0 }}>无法解析 PDF 地址</p>
          </Card>
        </div>
      </LiteratureLimsShell>
    );
  }

  // 相对路径 /uploads/... 必须走 /api/uploads/...（与 setupProxy、backend 静态目录一致），
  // 否则开发环境会请求到 CRA(3000) 得到 index.html，pdf.js 会报 Invalid PDF structure。
  const raw = rawPdfSource;
  const pdfUrl =
    raw.startsWith('http://') || raw.startsWith('https://')
      ? raw
      : raw.startsWith('/uploads')
        ? `/api${raw}`
        : raw.startsWith('/')
          ? `${window.location.origin}${raw}`
          : `${window.location.origin}/${raw}`;

  const pdfTitle =
    literature.title.length > 72 ? `${literature.title.slice(0, 72)}…` : literature.title;

  return (
    <div className="lit-pdf-reader-bleed lit-pdf-font-head">
      <header className="lit-pdf-appbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, minWidth: 0 }}>
          <span className="lit-pdf-appbar__brand">文献精读</span>
          <nav className="lit-pdf-appbar__nav" aria-label="文献导航">
            <Link to="/literature" className={navLibrary ? 'lit-pdf-appbar__nav--active' : ''}>
              文献库
            </Link>
            <Link to="/literature/shared" className={navShared ? 'lit-pdf-appbar__nav--active' : ''}>
              共享文献
            </Link>
            <Link to="/literature/notebook" className={navNotebook ? 'lit-pdf-appbar__nav--active' : ''}>
              笔记本
            </Link>
          </nav>
        </div>
        <div className="lit-pdf-appbar__right">
          <div className="lit-pdf-search-shell">
            <SearchOutlined style={{ color: 'var(--lit-pdf-on-variant)', fontSize: 16 }} />
            <input
              type="search"
              value={protocolSearch}
              onChange={(e) => setProtocolSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  message.info('书目检索请使用文献列表页；当前为 PDF 精读模式。');
                }
              }}
              placeholder="搜索文献、关键词…"
            />
          </div>
        </div>
      </header>

      <div className="lit-pdf-toolbar lit-pdf-toolbar--1">
        <div className="lit-pdf-toolbar__left">
          <button type="button" className="lit-pdf-back" onClick={inspectBack}>
            <ArrowLeftOutlined />
            返回检视
          </button>
          <span className="lit-pdf-divider-v" aria-hidden />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            {pdfList.length > 1 ? (
              <Select
                className="lit-pdf-select"
                size="small"
                style={{ minWidth: 200 }}
                value={attachmentIdFromUrl || pdfList[0]?.id}
                options={pdfList.map((p) => ({ label: p.name, value: p.id }))}
                onChange={(v) => setSearchParams({ attachmentId: v }, { replace: true })}
                placeholder="切换 PDF 附件"
              />
            ) : (
              <span
                style={{ fontSize: 11, fontWeight: 700, color: 'var(--lit-pdf-on-surface)', maxWidth: 'min(400px, 42vw)' }}
                className="lit-pdf-font-head"
              >
                {pdfTitle}
              </span>
            )}
            {literature.articleUrl?.trim() ? (
              <button type="button" className="lit-pdf-link-ghost" onClick={() => openArticlePageUrl(literature.articleUrl || '')}>
                打开原文链接 <LinkOutlined style={{ fontSize: 12 }} />
              </button>
            ) : null}
          </div>
        </div>
        <div className="lit-pdf-toolbar__right">
          <div className="lit-pdf-reader-tools" role="toolbar" aria-label="阅读工具">
            <span className="lit-pdf-tool-label">阅读工具</span>
            <div className="lit-pdf-mode-pills" role="tablist" aria-label="阅读模式">
              <Tooltip title={readerMode === 'read' ? readerToolbarHint : '阅读模式'}>
                <button
                  type="button"
                  className={readerMode === 'read' ? 'lit-pdf-mode-pills--active' : ''}
                  onClick={() => setReaderMode('read')}
                >
                  <BookOutlined /> 阅读
                </button>
              </Tooltip>
              <Tooltip title={readerMode === 'translate' ? readerToolbarHint : '翻译模式'}>
                <button
                  type="button"
                  className={readerMode === 'translate' ? 'lit-pdf-mode-pills--active' : ''}
                  onClick={() => setReaderMode('translate')}
                >
                  <TranslationOutlined /> 翻译
                </button>
              </Tooltip>
              <Tooltip title={readerMode === 'annotate' ? readerToolbarHint : '标注模式'}>
                <button
                  type="button"
                  className={readerMode === 'annotate' ? 'lit-pdf-mode-pills--active' : ''}
                  onClick={() => setReaderMode('annotate')}
                >
                  <HighlightOutlined /> 标注
                </button>
              </Tooltip>
            </div>
            {readerMode === 'annotate' ? (
              <div className="lit-pdf-swatches">
                {ANNOT_COLORS.map((c, idx) => (
                  <button
                    key={c}
                    type="button"
                    className={`lit-pdf-swatch ${annotateColor === c ? 'lit-pdf-swatch--on' : ''}`}
                    aria-label={`标注颜色 ${idx + 1}`}
                    style={{ background: c }}
                    onClick={() => setAnnotateColor(c)}
                  />
                ))}
              </div>
            ) : null}
          </div>
          <div className="lit-pdf-zoom-cluster">
            <Tooltip title="缩小">
              <button type="button" onClick={handleZoomOut} disabled={scale <= 0.5}>
                <ZoomOutOutlined />
              </button>
            </Tooltip>
            <span className="lit-pdf-zoom-pct">{Math.round(scale * 100)}%</span>
            <Tooltip title="放大">
              <button type="button" onClick={handleZoomIn} disabled={scale >= 3.0}>
                <ZoomInOutlined />
              </button>
            </Tooltip>
            <span className="lit-pdf-divider-v" style={{ height: 12 }} aria-hidden />
            <button type="button" className="lit-pdf-reset" onClick={handleResetZoom}>
              重置
            </button>
          </div>
          {numPages ? (
            <div className="lit-pdf-page-indicator">
              第
              <input
                aria-label="跳转到页码"
                value={pageJumpDraft}
                onChange={(e) => setPageJumpDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onBlur={() => commitPageJump()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitPageJump();
                  }
                }}
              />
              / {numPages} 页
            </div>
          ) : null}
        </div>
      </div>

      <div className="lit-pdf-split">
        <div ref={pdfScrollRef} className="lit-pdf-canvas-wrap" onMouseUp={onPdfMouseUp}>
          {pdfLoading && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>正在加载PDF...</div>
            </div>
          )}
          <Document
            key={raw}
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
          >
            {numPages && Array.from(new Array(numPages), (el, index) => {
              const pageNo = index + 1;
              const pageAnnots = annotations
                .filter((a) => a.page === pageNo)
                .slice()
                .sort((a, b) => {
                  const ta = a.rects?.[0]?.top ?? 0;
                  const tb = b.rects?.[0]?.top ?? 0;
                  if (ta !== tb) return ta - tb;
                  return a.createdAt - b.createdAt;
                });
              return (
                <div key={`page_${pageNo}`} className="lit-pdf-page-row">
                  <div className="lit-pdf-page-sheet" data-pdf-page={pageNo}>
                    <Page
                      pageNumber={pageNo}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      width={(isMobile ? 360 : 720) * scale}
                    />
                    {pageAnnots
                      .filter((a) => Array.isArray(a.rects) && a.rects!.length > 0)
                      .flatMap((a) =>
                        a.rects!.map((r, ri) => (
                          <div
                            key={`${a.id}-${ri}`}
                            title={a.note || a.text.slice(0, 80)}
                            style={{
                              position: 'absolute',
                              left: `${r.left}%`,
                              top: `${r.top}%`,
                              width: `${r.width}%`,
                              height: `${r.height}%`,
                              background: a.color,
                              opacity: 0.42,
                              pointerEvents: 'none',
                              mixBlendMode: 'multiply',
                              borderRadius: 2,
                              zIndex: 12
                            }}
                          />
                        ))
                      )}
                    {readerMode === 'translate' &&
                    docTranslationAnchor &&
                    !translationBubbleDismissed &&
                    docTranslationAnchor.page === pageNo ? (
                      <div
                        className="lit-pdf-trans-pop"
                        style={{
                          position: 'absolute',
                          left: `${docTranslationAnchor.leftPct}%`,
                          top: `${docTranslationAnchor.topPct}%`,
                          width: `${docTranslationAnchor.widthPct}%`,
                          minWidth: 160,
                          maxWidth: '96%'
                        }}
                      >
                        <div className="lit-pdf-trans-pop__head">
                          <span>翻译 · 英 → 中</span>
                          <button type="button" aria-label="关闭译文浮层" onClick={() => setTranslationBubbleDismissed(true)}>
                            <CloseOutlined />
                          </button>
                        </div>
                        <div className="lit-pdf-trans-pop__body">
                          {translating ? (
                            <div>
                              <Spin size="small" /> <span style={{ marginLeft: 8, color: 'var(--lit-pdf-on-variant)' }}>翻译中…</span>
                            </div>
                          ) : translatedText ? (
                            <div style={{ whiteSpace: 'pre-wrap' }}>{translatedText}</div>
                          ) : (
                            <div style={{ color: 'var(--lit-pdf-on-variant)', fontSize: 12 }}>准备翻译…</div>
                          )}
                        </div>
                        <div className="lit-pdf-trans-pop__actions">
                          <button type="button" onClick={() => void handleCopyTranslation()}>
                            复制
                          </button>
                          <button type="button" onClick={handleSaveTranslationAsNote}>
                            写入笔记
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="lit-pdf-page-margin">
                    <div className="lit-pdf-page-margin__head">
                      <span>第 {pageNo} 页</span>
                      <span className="lit-pdf-page-margin__count">
                        {pageAnnots.length ? `${pageAnnots.length} 条标注` : '无标注'}
                      </span>
                    </div>
                    <div className="lit-pdf-page-margin__list">
                      {pageAnnots.length === 0 ? (
                        <div className="lit-pdf-page-margin__empty">
                          {readerMode === 'annotate' ? '在本页划选文本可添加标注' : '本页暂无标注'}
                        </div>
                      ) : (
                        pageAnnots.map((a) => (
                          <div key={a.id} className="lit-pdf-annot-card" style={{ borderLeft: `4px solid ${a.color}` }}>
                            <div className="lit-pdf-annot-card__top">
                              <span className="lit-pdf-annot-pill" style={{ background: `${a.color}55`, color: '#191c1d' }}>
                                p.{a.page ?? '?'}
                              </span>
                              <button type="button" className="lit-pdf-link-ghost" onClick={() => removeAnnotation(a.id)}>
                                <DeleteOutlined /> 删除
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                fontStyle: 'italic',
                                color: 'var(--lit-pdf-on-variant)',
                                marginBottom: 8,
                                lineHeight: 1.5
                              }}
                            >
                              {a.text.length > 180 ? `${a.text.slice(0, 180)}…` : a.text}
                            </div>
                            <Input.TextArea
                              value={a.note}
                              onChange={(e) => updateAnnotationNote(a.id, e.target.value)}
                              placeholder="上下文批注（可选）"
                              autoSize={{ minRows: 1, maxRows: 4 }}
                              style={{ fontSize: 12 }}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </Document>

          {orphanAnnotations.length > 0 ? (
            <div className="lit-pdf-orphan-annots">
              <div className="lit-pdf-page-margin__head">
                <span>未定位页码的标注</span>
                <span className="lit-pdf-page-margin__count">{orphanAnnotations.length} 条</span>
              </div>
              {orphanAnnotations.map((a) => (
                <div key={a.id} className="lit-pdf-annot-card" style={{ borderLeft: `4px solid ${a.color}` }}>
                  <div className="lit-pdf-annot-card__top">
                    <span className="lit-pdf-annot-pill" style={{ background: `${a.color}55`, color: '#191c1d' }}>
                      p.?
                    </span>
                    <button type="button" className="lit-pdf-link-ghost" onClick={() => removeAnnotation(a.id)}>
                      <DeleteOutlined /> 删除
                    </button>
                  </div>
                  <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--lit-pdf-on-variant)', marginBottom: 8, lineHeight: 1.5 }}>
                    {a.text.length > 180 ? `${a.text.slice(0, 180)}…` : a.text}
                  </div>
                  <Input.TextArea
                    value={a.note}
                    onChange={(e) => updateAnnotationNote(a.id, e.target.value)}
                    placeholder="上下文批注（可选）"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    style={{ fontSize: 12 }}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {!isMobile && sidebarCollapsed ? (
          <div className="lit-pdf-sidebar-collapsed-rail">
            <Tooltip title="展开研究侧栏">
              <button type="button" onClick={() => setSidebarCollapsed(false)}>
                <MenuUnfoldOutlined />
              </button>
            </Tooltip>
          </div>
        ) : (
          <aside className="lit-pdf-sidebar">
            <div className="lit-pdf-sidebar__notes">
              <div className="lit-pdf-sidebar__notes-head">
                <div className="lit-pdf-sidebar__notes-title">
                  <HistoryOutlined /> 研究笔记
                </div>
                {!isMobile ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Tooltip title="收起侧栏">
                      <button type="button" className="lit-pdf-back" style={{ padding: 4 }} onClick={() => setSidebarCollapsed(true)}>
                        <MenuFoldOutlined />
                      </button>
                    </Tooltip>
                  </div>
                ) : null}
              </div>
              <div className="lit-pdf-notes-card">
                <div className="lit-pdf-notes-actions">
                  <button type="button" className="lit-pdf-btn-primary" onClick={openNotesExpandEditor}>
                    <EditOutlined /> 编辑笔记
                  </button>
                  <button type="button" className="lit-pdf-btn-primary" disabled={savingNotes} onClick={() => void handleSaveNotes()}>
                    {savingNotes ? '保存中…' : '保存笔记'}
                  </button>
                </div>
                <div className="lit-pdf-notes-meta">
                  <span className="lit-pdf-autosave-hint">
                    {lastNotesSavedAt ? `上次保存 ${lastNotesSavedAt}` : '尚未保存到服务器'}
                  </span>
                </div>
              </div>
            </div>

            <div className="lit-pdf-sidebar__related">
              <div className="lit-pdf-related-head">
                <LinkOutlined /> 相关文献
              </div>
              <p className="lit-pdf-related-hint">与文献库条目互链后，双方检视与阅读页均可看到彼此。</p>
              <div className="lit-pdf-related-list">
                {(literature?.relatedLiteratures || []).length === 0 ? (
                  <p className="lit-pdf-related-empty">暂无关联文献</p>
                ) : (
                  (literature?.relatedLiteratures || []).map((r) => (
                    <div key={r._id} className="lit-pdf-related-row">
                      <div className="lit-pdf-related-row__main">
                        <div className="lit-pdf-related-row__title">{r.title}</div>
                        <div className="lit-pdf-related-row__meta">
                          {[r.year, r.journal].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <div className="lit-pdf-related-row__actions">
                        <button
                          type="button"
                          className="lit-pdf-link-ghost"
                          disabled={savingRelated}
                          onClick={() => navigate(`/literature/${r._id}/view`)}
                        >
                          打开
                        </button>
                        <button
                          type="button"
                          className="lit-pdf-link-ghost lit-pdf-related-remove"
                          disabled={savingRelated}
                          onClick={() => void handleRemoveRelated(r._id)}
                        >
                          移除
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button
                type="button"
                className="lit-pdf-btn-primary lit-pdf-related-add"
                disabled={!id || savingRelated}
                onClick={() => {
                  setPickKeyword('');
                  setSelectedToAddId(undefined);
                  setRelatedPickOpen(true);
                }}
              >
                <LinkOutlined /> 添加相关文献
              </button>
            </div>

            <div className="lit-pdf-sidebar__annot">
              <div className="lit-pdf-sidebar__annot-head">
                <div className="lit-pdf-sidebar__annot-title">标注（{annotations.length}）</div>
                <div className="lit-pdf-sidebar__annot-sub">
                  标注卡片显示在左侧对应论文页右侧，随页面滚动对齐；此处可清空或导出。
                </div>
              </div>
              {readerMode === 'translate' ? (
                <div style={{ fontSize: 11, color: 'var(--lit-pdf-on-variant)', lineHeight: 1.6, padding: '2px 0 8px' }}>
                  译文支持<strong>复制</strong>与<strong>写入笔记</strong>；语言方向当前为英译中。
                </div>
              ) : null}
              {annotations.length > 0 ? (
                <Popconfirm title="清空本文献在本机的全部标注？" okText="清空" cancelText="取消" onConfirm={clearAllAnnotations}>
                  <button type="button" className="lit-pdf-export" style={{ marginTop: 8 }}>
                    清空全部标注
                  </button>
                </Popconfirm>
              ) : null}
              <button type="button" className="lit-pdf-export" onClick={handleExportAnnotationSummary}>
                导出标注摘要 <DownloadOutlined />
              </button>
            </div>

            <div className="lit-pdf-sidebar-footer">
              <button type="button" onClick={() => setSettingsOpen(true)}>
                <SettingOutlined style={{ fontSize: 18 }} />
                <span>设置</span>
              </button>
              <button type="button" onClick={() => setHelpOpen(true)}>
                <QuestionCircleOutlined style={{ fontSize: 18 }} />
                <span>帮助</span>
              </button>
            </div>
          </aside>
        )}
      </div>

      <Modal
        title="从文献库添加相关文献"
        open={relatedPickOpen}
        destroyOnHidden
        onCancel={() => setRelatedPickOpen(false)}
        okText="添加"
        confirmLoading={savingRelated}
        okButtonProps={{ disabled: !selectedToAddId }}
        onOk={() => void handleConfirmAddRelated()}
        width={isMobile ? 320 : 480}
      >
        <p className="lit-pdf-related-pick-hint">仅可选择您本账号下的文献；保存后双方会互相出现在「相关文献」中。</p>
        <Select
          showSearch
          allowClear
          style={{ width: '100%' }}
          placeholder="输入关键词搜索…"
          filterOption={false}
          onSearch={(v) => setPickKeyword(v)}
          loading={pickLoading}
          value={selectedToAddId}
          onChange={(v) => setSelectedToAddId(v)}
          options={pickOptions
            .filter((l) => l._id !== id && !getRelatedIdsFromLiterature(literature).includes(l._id))
            .map((l) => ({
              value: l._id,
              label: `${l.title}${l.year != null ? ` (${l.year})` : ''}`
            }))}
          notFoundContent={pickLoading ? <Spin size="small" /> : '无匹配文献'}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <FileTextOutlined /> 编辑笔记
          </Space>
        }
        open={showExpandEditor}
        destroyOnHidden
        onCancel={() => setShowExpandEditor(false)}
        footer={
          <Space>
            <Button onClick={() => setShowExpandEditor(false)}>关闭</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingNotes}
              onClick={async () => {
                await handleSaveNotes();
                setShowExpandEditor(false);
              }}
            >
              保存
            </Button>
          </Space>
        }
        width={isMobile ? 360 : 960}
      >
        <div className="lit-pdf-notes-modal-editor-wrap">
          <RichTextEditor
            value={notesDraft}
            onChange={setNotesDraft}
            imageDisplayMode="inline"
            placeholder="随时记录阅读笔记，支持加粗、列表、链接与颜色等格式…"
            height={isMobile ? 320 : 400}
            showImage
          />
        </div>
      </Modal>

      <Modal title="阅读帮助" open={helpOpen} onCancel={() => setHelpOpen(false)} footer={<Button onClick={() => setHelpOpen(false)}>知道了</Button>}>
        <ul className="lit-pdf-help-list">
          <li>
            <strong>阅读</strong>：仅浏览；已有高亮在正文与侧栏始终可见，选中文本不会新增翻译或标注。
          </li>
          <li>
            <strong>翻译</strong>：划选后自动英译中，浮层可关闭、可复制或写入左侧研究笔记（富文本，含格式）；已有高亮同样始终可见。
          </li>
          <li>
            <strong>标注</strong>：划选可新增高亮；高亮与侧栏列表仅保存在本机浏览器，三种模式下均可查看与编辑批注；可「导出标注摘要」。
          </li>
          <li>阅读 / 翻译 / 标注切换在上方工具栏（放大缩小左侧）；缩放与页码跳转同栏；多附件文献可在文件名旁下拉切换。</li>
        </ul>
      </Modal>

      <Modal title="阅读器设置" open={settingsOpen} onCancel={() => setSettingsOpen(false)} footer={<Button onClick={() => setSettingsOpen(false)}>关闭</Button>}>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--lit-pdf-on-variant)', lineHeight: 1.65 }}>
          当前缩放：{Math.round(scale * 100)}%。界面遵循 Clinical Curator 设计：无硬分割线侧栏、主色渐变按钮与纸面阅读区。
        </p>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--lit-pdf-on-variant)', lineHeight: 1.65 }}>
          更多默认字号、主题等选项可在后续版本接入用户偏好。
        </p>
      </Modal>
    </div>
  );
};

export default PDFViewer;

